/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.pages

import io.peekandpoke.klang.Nav
import io.peekandpoke.klang.common.logging.LogArea
import io.peekandpoke.klang.common.logging.Logger
import io.peekandpoke.klang.comp.Motor
import io.peekandpoke.klang.comp.Oscilloscope
import io.peekandpoke.klang.comp.Spectrumeter
import io.peekandpoke.klang.comp.statusBarView
import io.peekandpoke.klang.layouts.MenuPickUp
import io.peekandpoke.klang.layouts.menuLessonTile
import io.peekandpoke.klang.pages.docs.tutorials.soundDesignBasicsTrack
import io.peekandpoke.klang.spine.Playbacks
import io.peekandpoke.klang.spine.Player
import io.peekandpoke.klang.spine.SongDeck
import io.peekandpoke.klang.storage.LookStorage
import io.peekandpoke.klang.ui.comp.KlangButton
import io.peekandpoke.klang.ui.comp.KlangControlStyles
import io.peekandpoke.klang.ui.comp.KlangGauge
import io.peekandpoke.klang.ui.comp.KlangIconToggle
import io.peekandpoke.klang.ui.comp.KlangIgnition
import io.peekandpoke.klang.ui.comp.KlangScreen
import io.peekandpoke.klang.ui.comp.KlangTextField
import io.peekandpoke.klang.ui.comp.KlangToolWindow
import io.peekandpoke.klang.ui.comp.KlangValueKnob
import io.peekandpoke.klang.ui.comp.KlangYesNoModal
import io.peekandpoke.klang.ui.feel.KlangLoadout
import io.peekandpoke.klang.ui.feel.KlangLookPreset
import io.peekandpoke.klang.ui.feel.KlangLookShelf
import io.peekandpoke.klang.ui.feel.KlangLookText
import io.peekandpoke.klang.ui.feel.KlangLooks
import io.peekandpoke.klang.ui.feel.KlangRole
import io.peekandpoke.klang.ui.feel.KlangRoleGroup
import io.peekandpoke.klang.ui.feel.KlangTheme
import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.dnd.DndManager.Companion.dnd
import io.peekandpoke.kraft.dnd.Drag
import io.peekandpoke.kraft.dnd.dropZone
import io.peekandpoke.kraft.modals.ModalsManager.Companion.modals
import io.peekandpoke.kraft.routing.JoinedPageTitle
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.html.css
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.html.onClick
import io.peekandpoke.ultra.html.onInput
import io.peekandpoke.ultra.html.onPointerDown
import kotlinx.browser.window
import kotlinx.css.Color
import kotlinx.css.px
import kotlinx.html.ButtonType
import kotlinx.html.FlowContent
import kotlinx.html.InputType
import kotlinx.html.Tag
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.h1
import kotlinx.html.input
import kotlinx.html.span
import kotlinx.html.textArea
import kotlinx.html.title
import org.khronos.webgl.Float32Array
import org.khronos.webgl.set
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import kotlin.math.PI
import kotlin.math.exp
import kotlin.math.roundToInt
import kotlin.math.sin

private val log = Logger(area = LogArea.Ui, source = "LooksPage")

@Suppress("FunctionName")
fun Tag.LooksPage(slot: Int?) = comp(
    LooksPage.Props(slot = slot)
) {
    LooksPage(it)
}

/**
 * The look editor (KLANG-00382): the equip grid and the bench side by side, and below them the look clicked last, its
 * roles in tiles by [KlangRoleGroup].
 *
 * **Equipping** is drag and drop, with Kraft's (ULTRA-00265): a look dragged onto a slot goes there
 * ([KlangLoadout.place], a swap from another slot), one dragged off its slot onto the bench comes off. A drop leaves
 * the selection as it is (the maintainer, 2026-10-31: "the drop should not call select()"). While a look is dragged,
 * the place under the pointer lights up ([DROP_LIGHT], [DROP_WASH]), no ring. A click on a look shows it for editing
 * (the gold ring); the pick and place by clicks of 2026-10-28 is gone (the maintainer, 2026-10-31: "remove the "in
 * hand" logic completely. Say somewhere "use drag and drop""). Only the drawer's "+" still fills by click: its slot
 * waits ([target]), and a look clicked on the bench goes there.
 *
 * **Live, then back:** while the page is open the app wears the look being edited, its draft included
 * ([KlangTheme.preview]); leaving, it goes back to the look worn. **Apply** keeps the draft in the look, **Wear it**
 * makes it the look worn, **Reset** drops the draft. A built-in look is read only: "Wear it" and "Duplicate to edit".
 * A draft not applied is kept when the page is left ([Drafts]): coming back shows it, still to apply or reset.
 */
class LooksPage(ctx: Ctx<Props>) : Component<LooksPage.Props>(ctx) {

    data class Props(
        /** The drawer's empty slot the user came from, to be filled by the next look picked; null for none. */
        val slot: Int?,
    )

    //  STATE  //////////////////////////////////////////////////////////////////////////////////////////////////

    private val shelf by subscribingTo(KlangLookShelf)

    /** The resolved look, draft included: the scope's and the spectrum's canvases take their colours from it. */
    private val look by subscribingTo(KlangTheme.look)

    /** The look clicked last, shown below for editing. */
    private var selectedId: String by value(KlangTheme.worn.id)

    /** An empty slot waiting to be filled by the next look clicked on the bench (from the drawer's "+"). */
    private var target: Int? by value(props.slot?.takeIf { it in 0 until KlangLoadout.SIZE })

    /** The selected look's roles as edited, not applied yet. */
    private var draft: Map<KlangRole, String> by value(Drafts.valuesOf(KlangTheme.worn))

    /** The selected look's name as edited. */
    private var draftName: String by value(Drafts.nameOf(KlangTheme.worn))

    /** The paste box: open, its text, and why the last paste was not a look. */
    private var pasteOpen: Boolean by value(false)
    private var pasteText: String by value("")
    private var pasteError: Boolean by value(false)

    private var copied: Boolean by value(false)

    /** The metal card's ignition key: where it points, a click turns it. */
    private var ignitionPreview: KlangIgnition.Position by value(KlangIgnition.Position.Off)

    /** The knob card's preview turns, so its arc shows; the gauge card's needle follows it. */
    private var knobPreview: Double by value(0.6)

    /** A look on its way to a slot or the bench, by id: a class of its own, as drop zones match payloads by class. */
    private class DraggedLook(val id: String)

    /** The place a dragged look is over and may be dropped on: a slot's or the bench's [DROP_ATTR] value; null for none. */
    private var dropOver: String? by value(null)

    init {
        lifecycle {
            onMount { KlangTheme.preview(draft) }
            onUnmount { KlangTheme.endPreview() }
        }

        // Every slot takes a look dragged from the bench or another slot (a swap from another slot), the bench one
        // dragged off a slot
        for (slot in 0 until KlangLoadout.SIZE) {
            dropPlace(name = "slot-$slot", accepts = { it.source != slot }) { look -> equip(look.id, slot) }
        }

        dropPlace(name = "bench", accepts = { it.source is Int }) { look ->
            shelf.loadout.slotOf(look.id)?.let { KlangLookShelf.unequip(it) }
        }
    }

    /** A drop zone on the element whose [DROP_ATTR] is [name]: lit while a look is over it, [drop] on a drop. */
    private fun dropPlace(name: String, accepts: (Drag<DraggedLook>) -> Boolean, drop: (DraggedLook) -> Unit) {
        dropZone<DraggedLook>(element = { dom?.querySelector("[$DROP_ATTR='$name']") }, accepts = accepts) {
            onEnter { dropOver = name }
            onLeave { if (dropOver == name) dropOver = null }
            onDragStop { dropOver = null }
            // The selection stays: a drop only moves a look
            onDrop { drag -> drop(drag.payload) }
        }
    }

    //  ACTIONS  ////////////////////////////////////////////////////////////////////////////////////////////////

    private val selected: KlangLookPreset get() = shelf.byId(selectedId) ?: KlangLooks.Classic

    private val dirty: Boolean get() = selected.own && (draft != selected.values || draftName != selected.name)

    private fun select(look: KlangLookPreset) {
        selectedId = look.id
        draft = Drafts.valuesOf(look)
        draftName = Drafts.nameOf(look)
        copied = false
        KlangTheme.preview(draft)
    }

    private fun edit(role: KlangRole, value: String?) {
        draft = when {
            value == null -> draft - role
            role.accepts(value) -> draft + (role to value)
            else -> return
        }

        Drafts.keep(selected, name = draftName, values = draft)
        KlangTheme.preview(draft)
    }

    private fun rename(name: String) {
        draftName = name.take(KlangLookPreset.NAME_MAX)
        Drafts.keep(selected, name = draftName, values = draft)
    }

    /** A click on a look, in a slot ([slot]) or on the bench (null): shown for editing; a waiting slot takes a bench look. */
    private fun pick(look: KlangLookPreset, slot: Int?) {
        val waiting = target

        if (waiting != null && slot == null) {
            equip(look.id, waiting)
        }

        select(look)
    }

    /**
     * [id] on [slot], by a drop or onto the waiting slot: a slot that waited for a look is filled, so it waits no more
     * (else the next look clicked on the bench would take that slot from the one just placed).
     */
    private fun equip(id: String, slot: Int) {
        KlangLookShelf.place(id, slot)

        if (target == slot) {
            target = null
        }
    }

    private fun apply(): KlangLookPreset {
        val kept = selected.copy(name = KlangLookText.cleanName(draftName), values = draft)

        KlangLookShelf.keep(kept)
        Drafts.forget(kept.id)
        draftName = kept.name

        if (KlangTheme.worn.id == kept.id) {
            KlangTheme.choose(kept)
        }

        return kept
    }

    private fun wear() {
        val look = if (dirty) apply() else selected

        KlangTheme.choose(look)
        LookStorage.save(look)
        KlangTheme.preview(draft)
    }

    private fun reset() {
        Drafts.forget(selectedId)
        select(selected)
    }

    /** A look of the user's own from [values], kept, selected, and on the waiting slot if there is one. */
    private fun create(name: String, values: Map<KlangRole, String>, author: String = "") {
        val look = KlangLookPreset(
            id = KlangLookShelf.newId(),
            name = freeName(KlangLookText.cleanName(name)),
            values = values,
            author = author,
            own = true,
        )

        KlangLookShelf.keep(look)
        target?.let { equip(look.id, it) }
        select(look)
    }

    private fun duplicate() {
        create(name = selected.name, values = if (selected.own) draft else selected.values)
    }

    /** [name], or with the first number that makes it a name no look has: "Sun", "Sun 2", "Sun 3"; cut to fit. */
    private fun freeName(name: String): String {
        val taken = shelf.all.map { it.name }.toSet()

        return generateSequence(1) { it + 1 }
            .map { n -> if (n == 1) name else name.take(KlangLookPreset.NAME_MAX - " $n".length).trim() + " $n" }
            .first { it !in taken }
    }

    private fun askToDelete() {
        val look = selected

        modals.show<Boolean> { handle ->
            KlangYesNoModal(
                handle = handle,
                headline = "Delete \"${look.name}\"?",
                content = { +"The look is gone for good, and its slot is empty. A copy you shared stays a copy." },
                yesText = "Delete",
                noText = "Keep it",
                onResult = { yes ->
                    if (yes) {
                        KlangLookShelf.delete(look.id)
                        Drafts.forget(look.id)

                        if (KlangTheme.worn.id == look.id) {
                            KlangTheme.choose(KlangLooks.Classic)
                            LookStorage.save(KlangLooks.Classic)
                        }

                        select(KlangTheme.worn)
                    }
                },
            )
        }
    }

    /** Copies the look as text; "Copied" only once it is. Outside a secure context there is no clipboard. */
    private fun copyAsText() {
        val clipboard = window.navigator.asDynamic().clipboard ?: return
        val look = selected.copy(name = KlangLookText.cleanName(draftName), values = draft)

        clipboard.writeText(KlangLookText.encode(look)).then(
            { _: dynamic -> copied = true },
            { e: dynamic -> log.warn("Could not copy the look", e) },
        )
    }

    private fun paste() {
        val look = KlangLookText.decode(pasteText, id = KlangLookShelf.newId())

        if (look == null) {
            pasteError = true

            return
        }

        create(name = look.name, values = look.values, author = look.author)
        pasteOpen = false
        pasteText = ""
        pasteError = false
    }

    /** What [role] shows: the draft's value, else the role it follows (as drafted), else its default. */
    private fun effective(role: KlangRole): String =
        draft[role] ?: role.follows?.let { effective(it) } ?: role.default

    //  RENDER  /////////////////////////////////////////////////////////////////////////////////////////////////

    override fun VDom.render() {
        JoinedPageTitle { listOf("Looks") }

        div {
            key = "looks-page"
            css {
                put("box-sizing", "border-box")
                put("height", "100%")
                put("overflow", "auto")
                put("padding", "${PAGE_PADDING_TOP_PX}px 32px")
                put("color", "var(--kl-ink-text)")
            }

            h1(classes = KlangControlStyles.heading.toString()) {
                css {
                    put("margin", "0 0 18px")
                    put("font-size", "13px")
                }

                +"Looks"
            }

            div {
                css {
                    put("display", "grid")
                    put("grid-template-columns", "minmax(0, 1fr) minmax(0, 1fr)")
                    put("gap", "18px")
                    put("align-items", "start")
                }

                renderEquipped()
                renderBench()
            }

            renderHint()
            renderEditor()
        }
    }

    private fun FlowContent.panel(title: String, content: FlowContent.() -> Unit) {
        div {
            css {
                put("background", "var(--kl-ground-raised)")
                put("border", "1px solid var(--kl-edge)")
                put("border-radius", "6px")
                put("padding", "16px")
                put("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.05)")
            }

            div(classes = KlangControlStyles.heading.toString()) {
                css { put("margin-bottom", "12px") }
                +title
            }

            content()
        }
    }

    private fun FlowContent.renderEquipped() {
        panel("Equipped, as the drawer shows them") {
            div {
                css {
                    put("display", "grid")
                    put("grid-template-columns", "repeat(4, minmax(0, 1fr))")
                    put("gap", "8px")
                }

                shelf.equipped.forEachIndexed { slot, look ->
                    if (look != null) {
                        renderLook(look, slot)
                    } else {
                        renderEmptySlot(slot)
                    }
                }
            }
        }
    }

    private fun FlowContent.renderBench() {
        panel("The bench") {
            div {
                css {
                    put("display", "grid")
                    put("grid-template-columns", "repeat(auto-fill, minmax(96px, 1fr))")
                    put("gap", "8px")
                    put("border-radius", "var(--kl-radius-domed)")

                    // A look dragged off its slot over the bench: a faint wash, no ring
                    if (dropOver == "bench") {
                        put("background", DROP_WASH)
                    }
                }
                attributes[DROP_ATTR] = "bench"

                for (look in shelf.bench) {
                    renderLook(look, slot = null)
                }

                dashedKey(label = "+ New look", action = { create(name = "Mine", values = KlangTheme.worn.values) })
            }
        }
    }

    private fun FlowContent.renderLook(look: KlangLookPreset, slot: Int?) {
        val chosen = look.id == selectedId

        button(type = ButtonType.button) {
            key = "look-${look.id}"
            title = look.name
            // The look shown for editing: the current one of the choice, not a toggle (a click does not undo it)
            if (chosen) {
                attributes["aria-current"] = "true"
            }

            attributes["aria-label"] = look.name
            slot?.let { attributes[DROP_ATTR] = "slot-$it" }
            onClick { pick(look, slot) }
            // A drag starts past a few pixels; a press that stays put is the click above
            onPointerDown { e -> dnd.startDrag(e, payload = DraggedLook(look.id), source = slot, preview = { lookChip(look) }) }

            css {
                put("display", "flex")
                put("flex-direction", "column")
                put("align-items", "center")
                put("justify-content", "center")
                put("gap", "4px")
                put("height", "56px")
                put("min-width", "0")
                put("padding", "4px 6px")
                put("border-radius", "var(--kl-radius-domed)")
                put("border", "1px solid var(--kl-edge)")
                put("background", "radial-gradient(circle at 50% 0%, var(--kl-cap-light), var(--kl-cap-dark) 70%)")
                put("color", "var(--kl-ink-strong)")
                put("font-family", "inherit")
                put("font-size", "12px")
                put("cursor", "pointer")
                put("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.12), 0 2px 4px rgba(0,0,0,0.5)")
                // A touch drags the look instead of scrolling the page, and a long press selects nothing
                put("touch-action", "none")
                put("user-select", "none")
                put("-webkit-user-select", "none")
                put("-webkit-touch-callout", "none")

                if (chosen) {
                    put("outline", "2px solid var(--kl-lamp-on)")
                    put("outline-offset", "1px")
                }

                // A dragged look over its slot: the slot lights up, no ring, so it does not look picked (the maintainer,
                // 2026-10-31: "This should not happen when dragging")
                if (slot != null && dropOver == "slot-$slot") {
                    put("filter", DROP_LIGHT)
                }
            }

            span {
                attributes["aria-hidden"] = "true"
                css {
                    put("width", "14px")
                    put("height", "14px")
                    put("border-radius", "50%")
                    put("border", "1px solid var(--kl-edge)")
                    put("background", "radial-gradient(circle at 40% 30%, ${look.swatch.joinToString(", ")})")
                }
            }

            span {
                css {
                    put("max-width", "100%")
                    put("overflow", "hidden")
                    put("text-overflow", "ellipsis")
                    put("white-space", "nowrap")
                }
                +look.name
            }
        }
    }

    /** What a dragged look shows at the pointer: its swatch and its name, as its tile does. */
    private fun FlowContent.lookChip(look: KlangLookPreset) {
        div {
            css {
                put("display", "flex")
                put("align-items", "center")
                put("gap", "6px")
                put("padding", "6px 10px")
                put("border-radius", "var(--kl-radius-domed)")
                put("border", "1px solid var(--kl-edge)")
                put("background", "radial-gradient(circle at 50% 0%, var(--kl-cap-light), var(--kl-cap-dark) 70%)")
                put("color", "var(--kl-ink-strong)")
                put("font-size", "12px")
                put("white-space", "nowrap")
                put("box-shadow", "0 6px 14px rgba(0,0,0,0.6)")
                put("opacity", "0.9")
            }

            span {
                css {
                    put("width", "14px")
                    put("height", "14px")
                    put("border-radius", "50%")
                    put("border", "1px solid var(--kl-edge)")
                    put("background", "radial-gradient(circle at 40% 30%, ${look.swatch.joinToString(", ")})")
                }
            }

            +look.name
        }
    }

    private fun FlowContent.renderEmptySlot(slot: Int) {
        val waiting = target == slot

        // Not a key: only a drop fills it (or a bench look clicked while it waits, from the drawer's "+")
        val name = if (waiting) "Empty slot, waiting for a look" else "Empty slot"

        div {
            key = "slot-$slot"
            title = name
            attributes[DROP_ATTR] = "slot-$slot"

            css {
                put("height", "56px")
                put("border-radius", "var(--kl-radius-domed)")
                put("border", "1px dashed " + if (waiting) "var(--kl-focus)" else "var(--kl-cap-light)")
                // A dragged look over it: a faint wash
                put("background", if (dropOver == "slot-$slot") DROP_WASH else "transparent")
                put("display", "flex")
                put("align-items", "center")
                put("justify-content", "center")
                put("color", "var(--kl-ink-label)")
                put("font-size", "16px")
            }

            // A plain div's title is not read out: the name goes in as hidden text
            span(classes = KlangControlStyles.visuallyHidden.toString()) { +name }

            span {
                attributes["aria-hidden"] = "true"
                +"+"
            }
        }
    }

    private fun FlowContent.dashedKey(label: String, action: () -> Unit) {
        button(type = ButtonType.button) {
            key = "key-$label"
            onClick { action() }

            css {
                put("height", "56px")
                put("padding", "4px 8px")
                put("border-radius", "var(--kl-radius-domed)")
                put("border", "1px dashed var(--kl-cap-light)")
                put("background", "transparent")
                put("color", "var(--kl-ink-label)")
                put("font-family", "inherit")
                put("font-size", "12px")
                put("cursor", "pointer")
            }

            +label
        }
    }

    private fun FlowContent.renderHint() {
        val waiting = target

        div {
            css {
                put("min-height", "20px")
                put("margin", "10px 2px 0")
                put("font-size", "12px")
                put("color", "var(--kl-ink-label)")
            }

            when {
                waiting != null -> +"Slot ${waiting + 1} waits: click a look on the bench, drag one there, or make a new one."
                else -> +"Use drag and drop: drag a look onto a slot to equip it, onto the bench to take it off. Click a look to edit it."
            }
        }
    }

    private fun FlowContent.renderEditor() {
        val look = selected
        val own = look.own

        // Stays at the top while the cards scroll under it, so Apply and the other keys stay in reach (the maintainer,
        // 2026-10-28); a band of the page's ground above it hides what scrolls past ("the content scrolls past it and
        // is then visible above the sticky panel"), up to the page's top edge, its padding included
        div {
            css {
                put("position", "sticky")
                put("top", "-${PAGE_PADDING_TOP_PX}px")
                put("z-index", "10")
                put("margin-top", "0")
                put("padding-top", "${PAGE_PADDING_TOP_PX}px")
                put("background", "var(--kl-ground-app)")
            }

            panel(if (own) "Your look" else "Built-in look, read only") {
                renderNameRow(look)

                if (pasteOpen) {
                    renderPasteBox()
                }

                renderKeys(look)
            }
        }

        for (group in KlangRoleGroup.entries) {
            renderGroup(group, editable = own)
        }
    }

    /** The look's swatch, its name (a field for one's own), and how it stands: changed, saved, worn. */
    private fun FlowContent.renderNameRow(look: KlangLookPreset) {
        div {
            css {
                put("display", "flex")
                put("align-items", "center")
                put("gap", "12px")
            }

            span {
                attributes["aria-hidden"] = "true"
                css {
                    put("flex", "none")
                    put("width", "26px")
                    put("height", "26px")
                    put("border-radius", "50%")
                    put("border", "1px solid var(--kl-edge)")
                    put("background", "radial-gradient(circle at 40% 30%, ${look.copy(values = draft).swatch.joinToString(", ")})")
                }
            }

            if (look.own) {
                div {
                    css { put("width", "200px") }
                    KlangTextField(
                        label = "The look's name",
                        value = draftName,
                        onInput = { rename(it) },
                        suffix = "${draftName.length}/${KlangLookPreset.NAME_MAX}",
                    )
                }
            } else {
                span {
                    css {
                        put("font-size", "18px")
                        put("color", "var(--kl-ink-strong)")
                    }
                    +look.name
                }
            }

            span {
                css {
                    put("margin-left", "auto")
                    put("font-size", "12px")
                    put("color", if (dirty) "var(--kl-lamp-warning)" else "var(--kl-ink-label)")
                }
                +standing(look)
            }
        }
    }

    private fun standing(look: KlangLookPreset): String {
        val changes = KlangRole.entries.count { draft[it] != look.values[it] } + (if (draftName != look.name) 1 else 0)
        val worn = KlangTheme.worn.id == look.id

        return when {
            look.own && changes > 0 -> "$changes ${if (changes == 1) "change" else "changes"}, not applied yet"
            worn -> "Worn. The app wears it while you are here"
            look.own -> "Saved. The app wears it while you are here"
            else -> "Duplicate it to make it your own. The app wears it while you are here"
        }
    }

    /** The keys, in a sunken strip along the panel's foot, as a device's key row: editing left, sharing right. */
    private fun FlowContent.renderKeys(look: KlangLookPreset) {
        val own = look.own

        div {
            css {
                put("display", "flex")
                put("flex-wrap", "wrap")
                put("align-items", "center")
                put("gap", "8px")
                put("margin", "16px -16px -16px")
                put("padding", "12px 16px")
                put("background", "color-mix(in srgb, var(--kl-ground-sunken) 70%, transparent)")
                put("border-top", "1px solid var(--kl-edge)")
                // The panel's inner corner: its 6 px radius less its 1 px border
                put("border-radius", "0 0 5px 5px")
            }

            if (own) {
                KlangButton(onClick = { apply() }, availability = enabledIf(dirty)) { +"Apply" }
                KlangButton(onClick = { reset() }, availability = enabledIf(dirty)) { +"Reset" }
            }

            KlangButton(onClick = { wear() }, surface = KlangButton.Surface.Domed, pressed = KlangTheme.worn.id == look.id && !dirty) {
                +"Wear it"
            }

            KlangButton(onClick = { duplicate() }) { +if (own) "Duplicate" else "Duplicate to edit" }

            if (own) {
                KlangButton(onClick = { askToDelete() }) { +"Delete" }
            }

            span { css { put("flex", "1") } }

            KlangButton(onClick = { copyAsText() }) { +if (copied) "Copied" else "Copy as text" }
            KlangButton(onClick = { pasteOpen = !pasteOpen }, pressed = pasteOpen) { +"Paste a look" }
        }
    }

    private fun FlowContent.renderGroup(group: KlangRoleGroup, editable: Boolean) {
        div(classes = KlangControlStyles.heading.toString()) {
            key = "group-${group.name}"
            css { put("margin", "22px 0 10px") }
            +group.title
        }

        div {
            key = "cards-${group.name}"
            css {
                put("display", "grid")
                // Four cards to a row (the maintainer, 2026-10-28)
                put("grid-template-columns", "repeat(4, minmax(0, 1fr))")
                put("gap", "14px")
            }

            for (element in group.elements) {
                renderElement(element, editable = editable)
            }
        }
    }

    private fun enabledIf(on: Boolean) = if (on) KlangButton.Availability.Enabled else KlangButton.Availability.Disabled

    private fun FlowContent.renderPasteBox() {
        div {
            css {
                put("margin-top", "14px")
                put("display", "flex")
                put("flex-direction", "column")
                put("gap", "8px")
                put("max-width", "560px")
            }

            textArea {
                attributes["aria-label"] = "A look as text"
                attributes["placeholder"] = "Paste a look's text here"
                attributes["rows"] = "4"
                css {
                    put("background", "var(--kl-ground-sunken)")
                    put("color", "var(--kl-ink-text)")
                    put("border", "1px solid var(--kl-edge)")
                    put("border-radius", "var(--kl-radius)")
                    put("padding", "8px")
                    put("font-family", "monospace")
                    put("font-size", "12px")
                }

                onInput { e ->
                    (e.target as? HTMLTextAreaElement)?.let {
                        pasteText = it.value
                        pasteError = false
                    }
                }

                +pasteText
            }

            div {
                css {
                    put("display", "flex")
                    put("align-items", "center")
                    put("gap", "10px")
                }

                KlangButton(onClick = { paste() }, availability = enabledIf(pasteText.isNotBlank())) { +"Add the look" }

                if (pasteError) {
                    span {
                        css { put("color", "var(--kl-lamp-warning)") }
                        +"That is not a look's text."
                    }
                }
            }
        }
    }

    private fun FlowContent.renderElement(element: KlangRoleGroup.Element, editable: Boolean) {
        // A wide card, two tiles, its preview left and its controls right, the preview as tall as the card (the
        // maintainer, 2026-10-28: "the preview higher, so that the glow can be seen")
        val wide = element.id in WIDE_ELEMENTS

        div {
            key = "element-${element.id}"
            css {
                if (wide) {
                    put("grid-column", "span 2")
                    put("display", "grid")
                    put("grid-template-columns", "minmax(0, 1fr) minmax(0, 1fr)")
                }

                put("background", "var(--kl-ground-raised)")
                put("border", "1px solid var(--kl-edge)")
                put("border-radius", "6px")
                put("overflow", "hidden")
            }

            // The element's preview, centred on the panel's ground: a step up from the page, a step below the card's
            // lower part (the maintainer, 2026-10-28: the page's ground melted into it, the raised ground was too far)
            div {
                css {
                    put("display", "flex")
                    put("align-items", "center")
                    put("justify-content", "center")
                    put("gap", "14px")
                    put("background", "var(--kl-ground-panel)")

                    if (wide) {
                        put("min-height", "${Motor.SPECTRUM_HEIGHT_REM}rem")
                        put("padding", "12px 14px")
                        put("border-right", "1px solid var(--kl-edge)")
                    } else {
                        put("height", "96px")
                        put("border-bottom", "1px solid var(--kl-edge)")
                    }
                }

                renderPreview(element.id)
            }

            div {
                css { put("padding", "12px 14px") }

                div {
                    css {
                        put("margin-bottom", "6px")
                        put("font-size", "13px")
                        put("font-weight", "700")
                        put("color", "var(--kl-ink-strong)")
                    }
                    +element.title
                }

                for ((role, label) in element.roles) {
                    renderRole(role, label, editable)
                }
            }
        }
    }

    private fun FlowContent.renderRole(role: KlangRole, label: String, editable: Boolean) {
        val value = effective(role)
        val following = role.follows?.takeIf { role !in draft }
        val saved = selected.values[role]

        div {
            key = "role-${role.name}"
            css {
                put("display", "flex")
                put("align-items", "center")
                put("gap", "8px")
                put("min-height", "30px")
                if (following != null) {
                    put("opacity", "0.6")
                }
            }

            // The label, its undo beside it; what it follows below it, so neither breaks (the maintainer, 2026-10-28)
            span {
                css {
                    put("flex", "1")
                    put("display", "flex")
                    put("flex-direction", "column")
                    put("font-size", "13px")
                    put("line-height", "1.2")
                }

                span {
                    css {
                        put("display", "flex")
                        put("align-items", "center")
                        put("gap", "6px")
                    }
                    +label

                    // A changed role's own undo, beside its label: the controls never move (the maintainer, 2026-10-28)
                    if (editable && draft[role] != saved) {
                        button(type = ButtonType.button) {
                            title = "Undo this change"
                            attributes["aria-label"] = "Undo the change to ${roleLabel(role)}"
                            onClick { edit(role, saved) }

                            css {
                                put("display", "inline-flex")
                                put("align-items", "center")
                                put("justify-content", "center")
                                put("flex", "none")
                                put("width", "18px")
                                put("height", "18px")
                                put("padding", "0")
                                put("border", "1px solid var(--kl-ink-label)")
                                put("border-radius", "50%")
                                put("background", "transparent")
                                put("color", "var(--kl-ink-label)")
                                put("font-size", "11px")
                                put("line-height", "1")
                                put("cursor", "pointer")
                            }

                            +"\u21B6"
                        }
                    }
                }

                if (following != null) {
                    span {
                        css {
                            put("font-size", "11px")
                            put("line-height", "1.1")
                            put("color", "var(--kl-ink-label)")
                            put("white-space", "nowrap")
                        }
                        +"follows ${roleLabel(following).lowercase()}"
                    }
                }
            }

            when (role.kind) {
                KlangRole.Kind.Color -> renderColor(role, value, editable)
                KlangRole.Kind.Length -> renderAmount(role, value, editable, range = 0.0..24.0, step = 1.0, unit = "px")
                KlangRole.Kind.Amount -> renderAmount(role, value, editable, range = 0.0..1.0, step = 0.01, unit = "")
            }
        }
    }

    private fun FlowContent.renderColor(role: KlangRole, value: String, editable: Boolean) {
        input(type = InputType.color) {
            attributes["aria-label"] = roleLabel(role)
            this.value = sixDigits(value)
            disabled = !editable
            css {
                put("flex", "none")
                put("width", "32px")
                // As tall as a label with its hint under it, two lines (the maintainer, 2026-10-28)
                put("height", "${FIELD_HEIGHT_PX}px")
                put("box-sizing", "border-box")
                put("padding", "0")
                put("border", "1px solid var(--kl-edge)")
                put("border-radius", "var(--kl-radius)")
                put("background", "transparent")
                put("cursor", if (editable) "pointer" else "default")
            }

            onInput { e -> (e.target as? HTMLInputElement)?.let { edit(role, it.value) } }
        }

        input(type = InputType.text) {
            attributes["aria-label"] = "${roleLabel(role)}, hex"
            attributes["spellcheck"] = "false"
            this.value = value
            readonly = !editable
            css {
                put("flex", "none")
                put("width", "80px")
                put("height", "${FIELD_HEIGHT_PX}px")
                put("box-sizing", "border-box")
                put("padding", "0 6px")
                put("background", "var(--kl-ground-sunken)")
                put("color", "var(--kl-ink-text)")
                put("border", "1px solid var(--kl-edge)")
                put("border-radius", "var(--kl-radius)")
                put("font-family", "monospace")
                put("font-size", "12px")
            }

            // Only a value the role takes reaches the look; a half-typed one waits in the field
            onInput { e -> (e.target as? HTMLInputElement)?.let { edit(role, it.value.trim()) } }
        }
    }

    private fun FlowContent.renderAmount(
        role: KlangRole,
        value: String,
        editable: Boolean,
        range: ClosedFloatingPointRange<Double>,
        step: Double,
        unit: String,
    ) {
        val number = value.removeSuffix("px").toDoubleOrNull() ?: 0.0

        if (!editable) {
            span {
                css {
                    put("font-family", "monospace")
                    put("font-size", "12px")
                }
                +value
            }

            return
        }

        KlangValueKnob(
            name = roleLabel(role),
            value = number,
            range = range,
            step = step,
            onChange = { v ->
                val text = if (unit == "px") "${v.roundToInt()}px" else ((v * 1000).roundToInt() / 1000.0).toString()
                edit(role, text)
            },
            unit = unit,
            decimals = if (unit == "px") 0 else 2,
        )
    }

    private fun FlowContent.renderPreview(element: String) {
        when (element) {
            "grounds" -> div {
                css {
                    put("display", "flex")
                    put("width", "200px")
                    put("height", "64px")
                    put("border", "1px solid var(--kl-edge)")
                }

                div {
                    css {
                        put("width", "24px")
                        put("background", "var(--kl-ground-deep)")
                    }
                }

                div {
                    css {
                        put("flex", "1")
                        put("display", "flex")
                        put("align-items", "center")
                        put("justify-content", "center")
                        put("gap", "8px")
                        put("background", "var(--kl-ground-panel)")
                    }

                    swatchBox("var(--kl-ground-raised)", 40, 36)
                    swatchBox("var(--kl-ground-sunken)", 40, 20)
                }
            }

            "ink" -> div {
                css {
                    put("padding", "8px 12px")
                    put("background", "var(--kl-ground-panel)")
                    put("border", "1px solid var(--kl-edge)")
                    put("font-size", "12px")
                    put("line-height", "1.4")
                }

                div {
                    css {
                        put("color", "var(--kl-ink-strong)")
                        put("font-weight", "700")
                    }
                    +"Headline"
                }

                div {
                    css { put("color", "var(--kl-ink-text)") }
                    +"Body text"
                }

                div(classes = KlangControlStyles.heading.toString()) { +"Label" }
            }

            "corners" -> {
                KlangButton(onClick = {}) { +"Flat" }
                KlangButton(onClick = {}, surface = KlangButton.Surface.Domed) { +"Domed" }
            }

            "texture" -> {
                KlangButton(onClick = {}, surface = KlangButton.Surface.Domed, shape = KlangButton.Shape.Tile) { +"Grain" }

                div(classes = KlangControlStyles.workbenchColumn.toString()) {
                    css {
                        put("flex", "none")
                        put("width", "64px")
                        put("height", "56px")
                        put("border", "1px solid var(--kl-edge)")
                    }
                }
            }

            // The start page's ignition key, made of the look's metal; a click turns it, its grain with it
            "metal" -> KlangIgnition(
                position = ignitionPreview,
                glowing = false,
                // OFF, ON, START, and round again
                onTurn = {
                    val all = KlangIgnition.Position.entries
                    ignitionPreview = all[(ignitionPreview.ordinal + 1) % all.size]
                },
                size = 52.px,
                name = "Preview",
                cycling = true,
            )

            "caps" -> {
                KlangButton(onClick = {}, surface = KlangButton.Surface.Domed) { +"Key" }
                KlangButton(onClick = {}, surface = KlangButton.Surface.Domed, pressed = true) { +"Pressed" }
            }

            // The lamps, and under them the count badges: a strip key's corner and a tab's title in the error lamp's
            // colours, then the badge in each lamp's (the maintainer, 2026-11-03: "show the previews and the badges in
            // one 2x wide card", "Show the badge preview in all colors please")
            "lamps" -> div {
                css {
                    put("display", "flex")
                    put("flex-direction", "column")
                    put("align-items", "center")
                    put("gap", "18px")
                }

                div {
                    css {
                        put("display", "flex")
                        put("gap", "14px")
                    }

                    for (lamp in listOf("off", "on", "try", "warning", "critical")) {
                        span {
                            css {
                                put("width", "14px")
                                put("height", "14px")
                                put("border-radius", "50%")
                                put("background", "var(--kl-lamp-$lamp)")
                                put(
                                    "box-shadow",
                                    if (lamp == "off") "inset 0 1px 2px rgba(0,0,0,0.6)" else "0 0 8px var(--kl-lamp-$lamp)",
                                )
                            }
                        }
                    }
                }

                div {
                    css {
                        put("display", "flex")
                        put("align-items", "center")
                        put("gap", "16px")
                    }

                    KlangIconToggle(icon = { tools }, label = "Tools", on = false, large = true, count = 3, onClick = {})
                    KlangToolWindow(
                        title = "Tools",
                        pinned = true,
                        tabs = listOf(KlangToolWindow.Tab(id = "console", title = "Console", count = 12)),
                    ) {}
                }

                // The badge in each lamp's colour, under the lamps (the maintainer, 2026-11-03: "Show the badge preview
                // in all colors please"); the app's badges wear the error lamp
                div {
                    css {
                        put("display", "flex")
                        put("gap", "6px")
                    }

                    for ((index, lamp) in listOf("off", "on", "try", "warning", "critical").withIndex()) {
                        span(classes = KlangControlStyles.toolWindowTabCount.toString()) {
                            css {
                                put("--kl-badge-lamp", "var(--kl-lamp-$lamp)")
                                put("margin-left", "0")
                            }
                            +"${index + 1}"
                        }
                    }
                }
            }

            "accent" -> div {
                css {
                    put("width", "180px")
                    put("font-size", "12px")
                }

                div {
                    css {
                        put("padding", "4px 8px")
                        put("color", "var(--kl-accent)")
                    }
                    +"A link"
                }

                div {
                    css {
                        put("padding", "4px 8px")
                        put("background", "color-mix(in srgb, var(--kl-accent-muted) 35%, transparent)")
                        put("border-left", "2px solid var(--kl-accent-muted)")
                        put("color", "var(--kl-ink-strong)")
                    }
                    +"A selected row"
                }
            }

            "scrollbar" -> div {
                css {
                    put("width", "12px")
                    put("height", "72px")
                    put("padding", "2px")
                    put("box-sizing", "border-box")
                    put("background", "var(--kl-ground-sunken)")
                    put("border-radius", "6px")
                }

                // As the page shows it, at 65 %
                div {
                    css {
                        put("height", "40px")
                        put("margin-top", "14px")
                        put("border-radius", "4px")
                        put("background", "color-mix(in srgb, var(--kl-scroll-thumb-hover) 65%, transparent)")
                    }
                }
            }

            "focus" -> KlangButton(onClick = {}) {
                css {
                    put("outline", "2px solid var(--kl-focus)")
                    put("outline-offset", "2px")
                }
                +"Focused"
            }

            // The lesson tile of the Motor's menu, three of eight done (KLANG-00403)
            "progress" -> div {
                css { put("width", "200px") }

                menuLessonTile(
                    learning = MenuPickUp.Continue(
                        trackSlug = soundDesignBasicsTrack.slug,
                        title = soundDesignBasicsTrack.title,
                        next = soundDesignBasicsTrack.lessons[3].title,
                        done = 3,
                        total = soundDesignBasicsTrack.lessons.size,
                        target = Nav.tutorials(),
                    ),
                    onOpen = {},
                )
            }

            // The knob card's knob turns the gauge too (the maintainer, 2026-10-28)
            "gauge" -> KlangGauge(name = "Preview", value = knobPreview)

            "knob" -> KlangValueKnob(
                name = "Preview",
                value = knobPreview,
                range = 0.0..1.0,
                step = 0.01,
                onChange = { knobPreview = it },
                unit = "",
                decimals = 2,
            )

            "scope" -> div {
                css {
                    put("width", "220px")
                    put("height", "${Motor.OSCILLOSCOPE_HEIGHT_REM}rem")
                }

                Oscilloscope(
                    player = Player.player,
                    strokeColor = Color(look[KlangRole.ScopeTrace]),
                    preview = SCOPE_FRAME,
                )
            }

            "screen" -> div {
                css {
                    put("display", "flex")
                    put("width", "100%")
                    put("align-self", "stretch")
                }

                KlangScreen(size = KlangScreen.Size.Small, surface = "#000000", tube = true) {
                    // The editor's own class, so the glow reaches it as it reaches the code
                    div(classes = "cm-content") {
                        css {
                            put("padding", "8px 10px")
                            put("font-family", "monospace")
                            put("font-size", "12px")
                            put("line-height", "1.5")
                        }

                        div {
                            token(color = "#c678dd", text = "let ")
                            token(color = "#e5c07b", text = "feel")
                            token(color = "#abb2bf", text = " = 5")
                        }

                        div {
                            css { put("color", "#7f848e") }
                            +"// the tube glows"
                        }
                    }
                }
            }

            "lcd" -> div {
                span {
                    css {
                        put("padding", "4px 10px")
                        put("background", "var(--kl-ground-sunken)")
                        put("color", "var(--kl-lcd-ink)")
                        put("font-family", "monospace")
                        put("font-size", "18px")
                        put("border", "1px solid var(--kl-edge)")
                        put("border-radius", "var(--kl-radius)")
                    }
                    +"128"
                }

                // The status bar with a song on (KLANG-00406), on the Motor's dark screen as in place
                div {
                    css {
                        put("width", "240px")
                        put("margin-top", "12px")
                        put("background", "var(--kl-ground-sunken)")
                    }

                    statusBarView(
                        line = Playbacks.SongLine(
                            playing = SongDeck.Playing(songId = "a", title = "Gestrandet", isPlaying = true),
                            seconds = 83,
                        ),
                        idleText = "Klangmotor",
                        onOpen = {},
                        onStop = {},
                    )
                }
            }

            // As tall as the Motor's, so its cells are the Motor's
            "spectrum" -> div {
                css {
                    put("width", "100%")
                    put("height", "${Motor.SPECTRUM_HEIGHT_REM}rem")
                }

                Spectrumeter(
                    colors = Motor.SPECTRUM_ROLES.map { Color(look[it]) },
                    numBoxesInStack = Motor.SPECTRUM_BOXES,
                    colorStep = Motor.SPECTRUM_COLOR_STEP,
                    blend = Motor.spectrumBlendOf(look),
                    numBuckets = Motor.SPECTRUM_COLUMNS,
                    preview = SPECTRUM_LEVELS,
                ) { null }
            }
        }
    }

    private fun FlowContent.token(color: String, text: String) {
        span {
            css { put("color", color) }
            +text
        }
    }

    private fun FlowContent.swatchBox(background: String, width: Int, height: Int) {
        span {
            css {
                put("width", "${width}px")
                put("height", "${height}px")
                put("background", background)
                put("border", "1px solid var(--kl-edge)")
                put("border-radius", "var(--kl-radius)")
            }
        }
    }

    companion object {
        /** The page's padding at its top: the sticky look panel's band covers it, so nothing shows above the panel. */
        private const val PAGE_PADDING_TOP_PX = 28

        /** A colour's swatch and hex field: as tall as a role's label and its "follows" hint, 13 px and 11 px lines. */
        private const val FIELD_HEIGHT_PX = 28

        /** The cards two tiles wide, their preview left of their controls. */
        private val WIDE_ELEMENTS = setOf("screen", "spectrum", "lamps")

        /** The scope card's still frame: a tone with an overtone, as a song might show it. */
        private val SCOPE_FRAME = Float32Array(2048).also { frame ->
            for (i in 0 until frame.length) {
                val t = i / frame.length.toDouble()
                frame[i] = (0.5 * sin(2 * PI * 3 * t) + 0.15 * sin(2 * PI * 9 * t)).toFloat()
            }
        }

        /** The spectrum card's still levels: loud in the bass, falling to the top, the first columns into the peak band. */
        private val SPECTRUM_LEVELS = List(Motor.SPECTRUM_COLUMNS) { i ->
            0.85 * exp(-i / 11.0) + 0.06 * sin(i * 1.7) + 0.04
        }

        /** "GroundSunken" as "Ground sunken". */
        private fun roleLabel(role: KlangRole): String =
            role.name.replace(Regex("(?<=[a-z])(?=[A-Z])"), " ").lowercase().replaceFirstChar { it.uppercase() }

        /** The colour input takes `#rrggbb` only: a short one doubled, an alpha cut (shown whole in the hex field). */
        private fun sixDigits(hex: String): String {
            val digits = hex.removePrefix("#")

            return when (digits.length) {
                3, 4 -> "#" + digits.take(3).map { "$it$it" }.joinToString("")
                6, 8 -> "#" + digits.take(6)
                else -> "#000000"
            }.lowercase()
        }
    }
}

/** The attribute that names a place a look can be dropped on: `slot-<n>` or `bench`. */
private const val DROP_ATTR = "data-look-drop"

/** A look under a dragged one, where the drop would land: a little brighter, no ring. */
private const val DROP_LIGHT = "brightness(1.35)"

/** Where a dragged look would land, when no look is there to light up: the focus blue, faint. */
private const val DROP_WASH = "color-mix(in srgb, var(--kl-focus) 18%, transparent)"

/**
 * The drafts not applied yet, per look, kept while the app runs (KLANG-00382): leaving the editor keeps them, coming
 * back shows them, so leaving the page needs no question. A reload or a closed tab would lose them: the browser asks
 * first while one is pending, for the app's life. Not Kraft's `guardLeaving(beforeUnload = true)` (ULTRA-00266): it
 * holds only while its page is mounted, and the drafts outlive the page.
 */
private object Drafts {

    private val pending = mutableMapOf<String, Pair<String, Map<KlangRole, String>>>()

    /** The one listener, added with the first draft, for the app's life. */
    private var guarding = false

    fun nameOf(look: KlangLookPreset): String = pending[look.id]?.first ?: look.name

    fun valuesOf(look: KlangLookPreset): Map<KlangRole, String> = pending[look.id]?.second ?: look.values

    /** [name] and [values] as [look]'s draft; none when they are what it holds, or it is not one of the user's own. */
    fun keep(look: KlangLookPreset, name: String, values: Map<KlangRole, String>) {
        if (!look.own || (name == look.name && values == look.values)) {
            forget(look.id)

            return
        }

        pending[look.id] = name to values
        guard()
    }

    fun forget(id: String) {
        pending.remove(id)
    }

    private fun guard() {
        if (guarding) {
            return
        }

        guarding = true

        window.addEventListener("beforeunload", { e ->
            if (pending.isNotEmpty()) {
                e.preventDefault()
                e.asDynamic().returnValue = ""
            }
        })
    }
}
